import {
  FILE_TYPE,
  getDocChatOptions,
  INPUT_TYPES,
  LANGUAGE,
  LEGAL_CHATMODE,
  PAYLOAD_LANGUAGE,
  QUERY_TYPE,
  ROUTE_NAMES,
  WEB_SEARCH_MODEL,
} from "@root/constants";
import useSystem from "@root/hooks/useSystem";
import { PDF_PREVIEW, PDF_URL } from "@root/services/chat/endpoints";
import {
  getChatMaxSize,
  getChatMode,
  getFileIndex,
  getFileIndexType,
  getFilePopOver,
  getNotificationContent,
  getSelectedFiles,
  selectActiveThreadId,
} from "@root/store/selectors/entities";
import {
  getChatThreadId,
  getImagePopup,
  getSelectedModel,
  getThreadChatHistory,
  getThreadList,
  getThreadLoading,
  getThreadPID,
} from "@root/store/selectors/entities/thread";
import {
  getBaseUrl,
  getCurrentLanguage,
  getNotificationVisibility,
} from "@root/store/selectors/features";
import {
  filePopOverHandler,
  switchChat,
  updateChatMode,
  updateChatPage,
  updateChatPID,
  updateCurrentThreadId,
  updateFileIndex,
  updateImagePopup,
  updateSelectedModel,
  updateThreadChatById,
  updateThreadList,
  updateThreads,
} from "@root/store/slices/entities";
import {
  downloadPdfThread,
  getPdfPreviewThunk,
  // fetchNotifications,
  getRecentThreadHistory,
  getThreadByID,
  getThreadByKUID,
  getThreadByUID,
  getThreads,
  isPdfUrlValidThunk,
  sendInput,
  // updateNotification,
} from "@root/store/thunks";
import {
  doQuery,
  doQueryDrawingAnalysis,
  doQueryKCS,
  doQueryMultiDoc,
  doQueryProject,
  verifyChat,
} from "@root/store/thunks/chatThunks";
import { uploadFile } from "@root/store/thunks/userProfile";
import {
  addPdfIdToChatHistory,
  getChatRoute,
  safeJsonParse,
} from "@root/utils";
import {
  getSystemTypeChat,
  getSystemTypeChatItem,
} from "@root/utils/chatHelper";
import { generateRandomId } from "@root/utils/dateHelper";
import {
  calculateHasMore,
  calculateLastseen,
  findAllIndexes,
} from "@root/utils/helpers";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useProjectsDashboard from "./useProjectsDashboard";
import { useProjectsContext } from "@root/context/projects-context";
import { DocumentsService } from "@root/services/documentActions";

export const useMainChat = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const [input, setInput] = useState("");
  const loading = useSelector(getThreadLoading);
  const [isEditing, setIsEditing] = useState(false);
  const { isOnline } = useSystem();
  const currentThread: any = useSelector(getThreadList);
  const threadId = useSelector(selectActiveThreadId);
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const [docsHistory, setDocsHistory] = useState<any[]>([]);
  const [chatId, setChatId] = useState<any>(null);
  const maxChatSize = useSelector(getChatMaxSize);
  const filePopOver = useSelector(getFilePopOver);
  const chatMode = useSelector(getChatMode);
  const modeState = chatMode === "chat" ? true : false;
  const [docChatMode, setDocChatMode] = useState(getDocChatOptions()?.[0]);
  const [currentPathname, setCurrentPathname] = useState("");
  const [getDocumentsLoading, setGetDocumentsLoading] = useState(false);
  const [documentsData, setDocumentsData] = useState<any>(null);
  const [getSuggestedQuestionsLoading, setGetSuggestedQuestionsLoading] =
    useState(false);
  const [suggestedQuestionsData, setSuggestedQuestionsData] =
    useState<any>(null);
  const [pdfPageNumber, setPdfPageNumber] = useState<number>(1);
  const [getFaqsLoading, setGetFaqsLoading] = useState(false);
  const [faqsData, setFaqsData] = useState<any>(null);
  const [getChatsLoading, setGetChatsLoading] = useState(false);
  const [chatsData, setChatsData] = useState<any>(null);
  const [model, setModel] = useState(WEB_SEARCH_MODEL.CLAUDE_SONNET); // 1(claude) and 2 (midm)
  const [legalChatMode, setLegalMode] = useState(LEGAL_CHATMODE.WEB_SEARCH); // 1(Web search) and 2 (Law info center)
  const [review, setReview] = useState(FILE_TYPE.EXTRACTION_REVIEW);

  const fileIndex = useSelector(getFileIndex);
  const [chatHeadersData, setchatHeadersData] = useState({
    title: "",
    time: calculateLastseen(new Date()),
  });

  const { id, uid, thraedUID } = useParams();
  const inputRef: any = useRef<HTMLTextAreaElement>(null);
  const fileContainerRef = useRef<HTMLDivElement>(null);
  const fileIndexType = useSelector(getFileIndexType);
  const PDFs = useSelector(getSelectedFiles);
  const notificationContent = useSelector(getNotificationContent);
  const notificationVisibility = useSelector(getNotificationVisibility);
  const [selectedChat, setSelectedChat] = useState(null);

  const threadPID: any = useSelector(getThreadPID);
  const imagePopupState = useSelector(getImagePopup);
  const currentThreadId = useSelector(getChatThreadId);
  const [searchParams] = useSearchParams();
  const historyChatPID = searchParams.get("pid");
  const history = useSelector(getThreadChatHistory);

  const modelOptions = JSON.parse(process.env.REACT_APP_CHAT_MODELES ?? "[]");
  const modelOption = useSelector(getSelectedModel);
  const { id: threadIdFromUrl } = useParams();
  const { getFilename, documentData, projectsOptions } = useProjectsContext();

  const [currentMessageResponse, setCurrentMessageResponse] =
    useState<any>(null);
  const [isMessaging, setIsMessaging] = useState<any>(null);
  const [isWritingFirstPatch, setIsWritingFirstPatch] = useState(false);
  const [isTypewriterActive, setIsTypewriterActive] = useState(false);
  const [isPollingVerifyChat, setIsPollingVerifyChat] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [urlType, setUrlType] = useState<any>(null);
  const [urls, setUrls] = useState<any>([]); // Image urls
  const [url, setUrl] = useState<string | null>(null);
  const [filesPreview, setFilesPreview] = useState<any>([]);
  const [fileLoading, setFileLoading] = useState(false);
  const [flagValidateUrl, setFlagValidateUrl] = useState<boolean>(true);
  const [queryType, setQueryType] = useState<any>(null);
  const [chatByIdResponse, setChatByIdResponse] = useState<any>(null);
  const [recentThreads, setRecentThreads] = useState<any>([]);
  const [pdfValidLink, setPdfValidLink] = useState<string | null>(null); // PDF link returned by backend that won't throw CORS error
  const [pdfLinkLoading, setPdfLinkLoading] = useState<boolean>(false);
  const [deepSearch, setDeepSearch] = useState<string | null>(null);
  const [attachedFile, setAttachedFile] = useState<any[]>([]);
  const [attachedFileProject, setAttachedFileProject] = useState<any[]>([]);
  const [openProjectsListing, setOpenProjectsListing] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [sourcesData, setSourcesData] = useState(null);
  const [downloadPdfLoading, setDownloadPdfLoading] = useState(false);
  const [multiDocUploadView, setMultiDocUploadView] = useState(true);
  const [showWelcomeText, setShowWelcomeText] = useState(false);
  const [prevThread, setPrevThread] = useState<any>(null);

  const isPanelVisible =
    selectedChat ||
    queryType?.value === QUERY_TYPE.WEBSITE ||
    queryType?.value === QUERY_TYPE.PDF ||
    queryType?.value === QUERY_TYPE.YOUTUBE ||
    deepSearch === QUERY_TYPE.DEEP_SEARCH ||
    chatByIdResponse?.firstChat;

  const navigate = useNavigate();
  const currentLanguage = useSelector(getCurrentLanguage);

  const updateChatModeValue = (mode: "chat" | "string") => {
    dispatch(updateChatMode(mode));
  };

  // useEffect(() => {
  //   if (projectsOptions?.length && !selectedProject) {
  //     setSelectedProject(null);
  //   }
  // }, [projectsOptions]);

  const fetchHistory = (threadId: any) => {
    const onHistorySuccess = (res: any) => {
      const { pdfChats, userChats } = res;
      if (pdfChats && pdfChats?.length > 0) {
        let updatedHistories: any = [];
        pdfChats.forEach((chat: any) => {
          // const chatHistory = JSON.parse(chat?.answer)?.chat_history || [];

          const updatedChatHistory = addPdfIdToChatHistory(
            chatHistory,
            chat?.pdf_id
          );
          updatedHistories = [...updatedHistories, ...updatedChatHistory];
        });
        setDocsHistory(updatedHistories);
      } else {
        setDocsHistory([]);
      }
      if (userChats && userChats?.length > 0) {
        // setChatHistory(JSON.parse(userChats?.[0]?.answer)?.chat_history || []);
      } else {
        setChatHistory([]);
      }
    };
    const payload = {
      payload: { threadId: threadId || threadIdFromUrl },
      onSuccess: onHistorySuccess,
    };
    dispatch(getRecentThreadHistory(payload));
  };

  const onSingleThreadSuccess = (res: any) => {
    if (!res?.rows) {
      // navigate(ROUTE_NAMES.DASHBOARD);
    }
    setChatByIdResponse(res);
    const data = res?.rows?.[0];
    const model = data?.model;
    const modelOption = modelOptions?.find((item: any) => item.value === model);
    if (modelOption) {
      dispatch(updateSelectedModel(modelOption));
    }
    // setUrlType({ value: data?.url_type });
    // setUrls([data?.image_s3_url]);
    // setUrl(data?.url);
    if (uid) {
      dispatch(updateThreadList(getSystemTypeChat([res]) as any));
      setchatHeadersData({
        time: calculateLastseen(res?.createdAt),
        title: res?.question,
      });
      return;
    }

    dispatch(updateThreadList(getSystemTypeChat(res?.rows) as any));
    dispatch(updateCurrentThreadId(res?.rows?.[0]?.threadId));
    dispatch(updateChatPID(res?.rows?.[0]?.project_id));
    setChatId(res?.rows?.[0]?.chatId);
    if (thraedUID) {
      const [firstChat] = res?.rows;
      setchatHeadersData({
        time: calculateLastseen(firstChat?.createdAt),
        title: firstChat?.question || "",
      });
    } else {
      const firstChat = res?.firstChatDetails;
      if (firstChat) {
        setchatHeadersData({
          time: calculateLastseen(firstChat?.createdAt),
          title: firstChat?.question,
        });
      }
    }
    if (!uid && !thraedUID) {
      fetchHistory(res?.rows?.[0]?.threadId);
    }
  };

  const fetchThead = (renderPageCount = false) => {
    if (id) {
      if (calculateHasMore(currentThread, maxChatSize) || renderPageCount) {
        dispatch(
          getThreadByID({
            payload: { chatId: Number(id) },
            onSuccess: onSingleThreadSuccess,
            onError: () => null,
            pid: historyChatPID,
          })
        );
      }
    }
  };

  const fetchSharedThead = (renderPageCount = false) => {
    if (uid) {
      if (calculateHasMore(currentThread, maxChatSize) || renderPageCount) {
        dispatch(
          getThreadByUID({
            payload: { uid },
            onSuccess: onSingleThreadSuccess,
            onError: () => null,
          })
        );
      }
    }
  };

  const fetchSharedThead2 = (renderPageCount = false) => {
    if (thraedUID) {
      if (calculateHasMore(currentThread, maxChatSize) || renderPageCount) {
        dispatch(
          getThreadByKUID({
            payload: { thraedUID },
            onSuccess: onSingleThreadSuccess,
            onError: () => null,
          })
        );
      }
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(updateChatPage(1));
      dispatch(switchChat([]));
      setChatHistory([]);
      fetchThead(true);
      setUrl(null);
    } else {
    }
  }, [id]);

  useEffect(() => {
    if (uid) {
      dispatch(updateChatPage(1));
      dispatch(switchChat([]));
      setChatHistory([]);
      fetchSharedThead(true);
    } else {
    }
  }, [uid]);

  useEffect(() => {
    if (thraedUID) {
      dispatch(updateChatPage(1));
      dispatch(switchChat([]));
      setChatHistory([]);
      fetchSharedThead2(true);
    } else {
    }
  }, [thraedUID]);

  function scrollToBottom() {
    const chatParent = document.getElementById("chat_parent");
    if (chatParent) {
      chatParent.scrollTop = chatParent.scrollHeight;
    }
  }

  // useEffect(() => {
  //     if (!id) {
  //         if (!currentThreadId && !threadPID && !uid && !thraedUID) {
  //             navigate(ROUTE_NAMES.DASHBOARD)
  //         }
  //     }
  // }, [])

  const validator = () => {
    if (!isOnline) {
      return false;
    }
    if (loading) {
      return false;
    }
    if (!input.length || input.length === 0) {
      return false;
    }
    return true;
  };

  function parseChat() {
    try {
      if (currentThread?.[currentThread?.length - 1]?.meta?.chat_history) {
        return currentThread?.[currentThread?.length - 1]?.meta?.chat_history;
      }
      const meta = currentThread?.[currentThread?.length - 1]?.meta;
      const firstParse = JSON.parse(meta?.answer || meta?.c?.answer || "{}");
      // const secondParse = JSON.parse(firstParse);
      return firstParse?.chat_history || [];
    } catch (err) {
      return [];
    }
  }

  function getFilesPath() {
    if (currentPathname?.startsWith(ROUTE_NAMES.DRAWING_ANALYSIS)) {
      return;
    }
    if (currentPathname?.includes("checklist")) {
      return attachedFile?.map((file: any) => file?.s3_file_path);
    }
    return attachedFile?.[0]?.path;
  }

  function getDocNames() {
    if (currentPathname?.includes(ROUTE_NAMES.CHECKLIST)) {
      return attachedFile?.map((file: any) => file?.fileName);
    }
    return undefined;
  }

  const isLegalChatbot =
    currentPathname?.startsWith(ROUTE_NAMES.LEGAL_CHATBOT) ||
    currentPathname?.startsWith(ROUTE_NAMES.KSCE_CHAT_SCREEN);
  const isProjectChatbot =
    currentPathname?.startsWith(ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT) ||
    currentPathname?.startsWith(ROUTE_NAMES.DOCUMENT_CHAT);

  function getFileNamesArray() {
    if (isProjectChatbot && selectedProject) {
      return attachedFile?.length > 0
        ? [attachedFile?.[0]?.fileName]
        : undefined;
    }
    return undefined;
  }

  function getFileId() {
    if (isProjectChatbot && selectedProject) {
      return String(attachedFile?.[0]?.id)
    }
    if (currentPathname?.startsWith(ROUTE_NAMES.CHECKLIST) && attachedFile?.length) {
      return JSON.stringify(attachedFile?.map((file: any) => file?.id))
    }
    return undefined;
  }

  function getFileNameMain() {
    if (isLegalChatbot) {
      return attachedFile?.[0]?.fileName;
    }
    return getFilename();
  }

  function getSource() {
    if (isLegalChatbot && !attachedFile?.length) {
      return legalChatMode === LEGAL_CHATMODE.WEB_SEARCH
        ? LEGAL_CHATMODE.WEB_SEARCH.toString()
        : LEGAL_CHATMODE.LAW_INFO_CENTER.toString();
    }
  }

  function getMode() {
    if (currentPathname?.includes("document")) {
      return selectedProject ? Number(selectedProject) : null;
    }
    return undefined;
  }

  function getModel() {
    if (
      currentPathname?.startsWith("/legal") ||
      currentPathname?.startsWith("/chat")
    ) {
      return String(model);
    }
    return undefined;
  }

  function getPdfPageNum() {
    if (currentPathname?.startsWith(ROUTE_NAMES.DRAWING_ANALYSIS)) {
      return pdfPageNumber;
    }
    return undefined;
  }

  function getPdfId() {
    if (currentPathname?.startsWith(ROUTE_NAMES.DRAWING_ANALYSIS)) {
      return documentData?.id;
    }
    return undefined;
  }

  const handleVerifyChat = () => {
    dispatch(
      verifyChat({
        payload: {
          query: currentMessageResponse?.question,
          threadId: currentMessageResponse?.threadId,
          chatId: currentMessageResponse?.id,
          chat_history: parseChat(),
          law_generated_answer: safeJsonParse(currentMessageResponse?.answer)
            ?.answer,
          law_chat_id: currentMessageResponse?.id,
        },
        onSuccess: (data: any) => {
          console.log("verifychat success data: ", data);
        },
      })
    );
  };

  const submitHandler = (query?: string, bypassValidator: boolean = false) => {
    // if (!validator() && !bypassValidator) {
    //   return;
    // }
    setIsWritingFirstPatch(true);
    setIsMessaging(true);
    const id = generateRandomId("chat");
    const queryPayload = {
      payload: {
        pdf_id: getPdfId(),
        query: query || input,
        threadId: currentThreadId || undefined,
        chat_history: parseChat(),
        source: getSource(),
        mode: getMode()?.toString(),
        project_id: getMode(),
        s3_file_path: getFilesPath(),
        doc_name: getDocNames(),
        fileName: getFileNameMain(),
        file_name: getFileNamesArray(), // Currently relevant in project document chatbot
        file_id: getFileId(), // Currently relevant in project document chatbot and multi-doc analysis
        page_no: getPdfPageNum(),
        model: getModel(),
        language:
          currentLanguage === LANGUAGE.ENGLISH
            ? PAYLOAD_LANGUAGE.ENGLISH
            : PAYLOAD_LANGUAGE.KOREAN,
      },
      id,
      onSuccess: (data: any) => {
        setCurrentMessageResponse(data);
        if (currentThread?.length < 4) {
          dispatch(
            getThreads({
              onSuccess: (res: any) => dispatch(updateThreads(res)),
            })
          );
        }
      },
      onError: () => {
        setIsMessaging(false);
      },
    };
    if (currentPathname?.includes(ROUTE_NAMES.KCS)) {
      dispatch(doQueryKCS(queryPayload));
    } else if (currentPathname?.includes(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT)) {
      dispatch(doQueryDrawingAnalysis(queryPayload));
    } else if (currentPathname?.includes("document")) {
      dispatch(doQueryProject(queryPayload));
    } else if (currentPathname?.includes("checklist")) {
      dispatch(doQueryMultiDoc(queryPayload));
    } else {
      dispatch(doQuery(queryPayload));
    }
    setInput("");
    if (
      !currentPathname?.includes(ROUTE_NAMES.CHECKLIST) &&
      !isProjectChatbot
    ) {
      setAttachedFile([]);
    }
  };

  const onEditSuccess = ({ res, data: { chat, input } }: any) => {
    const childChatId = generateRandomId("childChat");
    let storePayload: any = {
      id: chat?.id,
      merge: true,
    };
    const customPrevChat = {
      PID: chat?.pid,
      id: childChatId,
      input,
    };
    storePayload.payload = {
      editLoading: false,
      childChatIndex: chat?.childchats?.length ? chat?.childchats?.length : 0,
      childchats: [
        ...(chat?.childchats || []),
        getSystemTypeChatItem(res, customPrevChat),
      ],
    };
    storePayload.userPayload = {
      editLoading: false,
      childChatIndex: chat?.childchats?.length ? chat?.childchats?.length : 0,
      childchats: [
        ...(chat?.childchats || []),
        getSystemTypeChatItem(res, customPrevChat),
      ],
    };

    dispatch(updateThreadChatById(storePayload as any));
  };

  const onEditError = ({ data: { chat } }: any) => {
    let storePayload: any = {
      id: chat?.id,
      merge: true,
    };
    storePayload.payload = {
      editLoading: false,
    };
    storePayload.userPayload = {
      editLoading: false,
    };

    dispatch(updateThreadChatById(storePayload as any));
  };

  const editSubmitHandler = (data?: any) => {
    const { chat, input } = data;
    if (loading) {
      return;
    }
    setIsEditing(true);
    let storePayload: any = {
      id: chat?.id,
      merge: true,
    };
    storePayload.payload = {
      editLoading: true,
    };
    storePayload.userPayload = {
      editLoading: true,
    };

    dispatch(updateThreadChatById(storePayload as any));
    dispatch(
      sendInput({
        payload: {
          user_question: input || "",
          // chat_history: chatHistory,
          use_tool: false,
          threadId: chat?.threadId,
          parent_chat_id: chat?.chatId,
        },
        pid: chat?.pid,
        onSuccess: (res: any) => onEditSuccess({ res, data }),
        onError: () => onEditError({ data }),
      })
    );
  };

  const onChangehandler = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  useEffect(() => {
    if (
      (findAllIndexes(input, INPUT_TYPES.FORM).includes(input.length) &&
        findAllIndexes(input, INPUT_TYPES.FORM).includes(
          inputRef?.current?.selectionStart || 0 + 5
        )) ||
      findAllIndexes(input, INPUT_TYPES.FORM).includes(
        inputRef?.current?.selectionStart || 0 + 5
      )
    ) {
      dispatch(filePopOverHandler(true));
    } else {
      dispatch(filePopOverHandler(false));
    }
  }, [inputRef?.current?.selectionStart]);

  useEffect(() => {
    scrollToBottom();
  }, [currentThread?.length]);

  useEffect(() => {
    if (threadId === 0 || !threadId) {
      setChatHistory([]);
    }
  }, [threadId]);

  useEffect(() => {
    dispatch(updateFileIndex(1));
  }, [filePopOver]);

  useEffect(() => {
    if (fileContainerRef.current) {
      if (fileIndex > 4 && fileIndexType === "inc") {
        fileContainerRef.current.scrollTop =
          fileContainerRef.current.scrollTop + 65;
      }
      if (fileIndex > PDFs.length - 3 && fileIndexType === "dec") {
        fileContainerRef.current.scrollTop =
          fileContainerRef.current.scrollTop - 65;
      }
    }
  }, [fileIndex, fileIndexType]);

  // const handleCloseNotification = () => {
  //     if (notificationContent[0]?.id) {
  //         dispatch(updateNotification({
  //             id: notificationContent[0].id, is_read: true,
  //             onSuccess: () => {
  //                 dispatch(fetchNotifications());
  //             },
  //             onError: (error: any) => {
  //                 console.error('Failed to update notification:', error);
  //             },
  //         })
  //         );
  //     }
  // };

  const onCloseImagePopup = () => {
    dispatch(
      updateImagePopup({
        url: null,
      } as any)
    );
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFileLoading(true);
    function onSuccess(data: any) {
      setFileLoading(false);
      const fileObj = data?.[0];
      setUrls((prev: any) => [fileObj]);
    }
    function onError() {
      setFileLoading(false);
    }

    const file = acceptedFiles?.[0];
    setFilesPreview((prev: any) => [file]);
    const data = new FormData();
    data.append("file", file);
    dispatch(
      uploadFile({
        payload: data,
        onSuccess: onSuccess,
        onError: onError,
      })
    );
  }, []);

  function removeFile(file: File) {
    const filterFilesPreview = filesPreview?.filter(
      (img: File) => img.name !== file.name
    );
    setFilesPreview(filterFilesPreview);
    const filterUrls = urls?.filter((url: any) => {
      const fileName = url?.fileUrl?.split("/")?.pop();
      return fileName !== file?.name;
    });
    setUrls(filterUrls);
  }

  const getInitialThreads = () => {
    dispatch(
      getThreads({
        onSuccess: (res: any) => {
          setRecentThreads(res?.rows?.slice(0, 5));
        },
      })
    );
  };

  const baseUrl = useSelector(getBaseUrl);

  const fetchPdfPreview = (payload: any) => {
    dispatch(
      getPdfPreviewThunk({
        payload,
        onSuccess: (data: any) => {
          setPdfValidLink(`${baseUrl}/${PDF_PREVIEW}?url=${payload?.url}`);
        },
        onError: (err: any) => {
          setPdfValidLink(null);
        },
      })
    );
  };

  const downloadPdfOfThread = (payload: any) => {
    if (!payload?.chatId) {
      setDownloadPdfLoading(true);
    }
    dispatch(
      downloadPdfThread({
        payload,
        onSuccess: (data: any) => {
          window.location.href = data?.data?.signedUrl;
          setDownloadPdfLoading(false);
        },
        onError: (err: any) => {
          setDownloadPdfLoading(false);
        },
      })
    );
  };

  const documentsService = new DocumentsService();

  const fetchAllDocs = async (payload: any) => {
    setGetDocumentsLoading(true);
    try {
      const res = await documentsService.getAllDocuments(payload);
      setDocumentsData(res?.data);
    } catch (err) {
    } finally {
      setGetDocumentsLoading(false);
    }
  };

  const fetchSuggestedQuestions = async (payload: any) => {
    setGetSuggestedQuestionsLoading(true);
    try {
      const res = await documentsService.getSuggestedQuestions(payload);
      setSuggestedQuestionsData(res?.data);
    } catch (err) {
    } finally {
      setGetSuggestedQuestionsLoading(false);
    }
  };

  const fetchFaqs = async (payload: any) => {
    setGetFaqsLoading(true);
    try {
      const res = await documentsService.getFaqs(payload);
      setFaqsData(res?.data);
    } catch (err) {
    } finally {
      setGetFaqsLoading(false);
    }
  };

  // Fetch chat history against pdf id
  // Currently used for chat history of a pdf in drawing analysis
  const fetchChats = async (payload: any) => {
    setGetChatsLoading(true);
    try {
      const res = await documentsService.getChats(payload);
      setChatsData(res?.data);
    } catch (err) {
    } finally {
      setGetChatsLoading(false);
    }
  };

  console.log("model", model);

  //like/dislike chat
  const likeChat = async (payload: any) => {
    try {
      const res = await documentsService.likeChat(payload);
      console.log(res);
    } catch (err) {
      console.error("Error liking/disliking chat: ", err);
    } finally {
    }
  };

  const resetData = (page: number = 1) => {
    setSelectedChat(null);
    setUrlType(null);
    setUrls([]);
    setFilesPreview([]);
    setQueryType(null);
    setChatByIdResponse(null);
    setPdfValidLink(null);
    setDeepSearch(null);
    setAttachedFile([]);
    setCurrentMessageResponse(null);
    dispatch(updateThreadList([] as any));
    setSourcesData(null);
    setPdfPageNumber(page);
    setShowWelcomeText(false);
    setIsMessaging(false);
  };

  // Projects Document Chatbot
  function toggleRightSidebar() {
    setOpenProjectsListing((prevState) => !prevState);
  }

  const onProjectChange = (isToggle: boolean = false) => {
    const currentThreadData = currentThread?.[0]?.meta?.c;
    if (currentThreadData && isToggle) {
      setPrevThread(currentThreadData);
    } else if (!currentThreadData) {
      setPrevThread(null);
    }
    resetData();
    setShowWelcomeText(true);
    if (currentPathname?.includes(ROUTE_NAMES.DOCUMENT_CHAT)) {
      navigate(ROUTE_NAMES.DOCUMENT_CHAT);
    } else {
      navigate(ROUTE_NAMES.KSCE_CHAT_SCREEN);
    }
    // OLD WORKING LOGIC TO MAINTAIN PREV THREAD CHAT ON TOGGLE. COMMENTED FOR NOW ON NEW FLOW.
    // if (prevThread && isToggle) {
    //   navigate(
    //     getChatRoute({ id: prevThread?.threadId, type: prevThread?.type })
    //   );
    // } else {
    //   setShowWelcomeText(true);
    //   if (currentPathname?.includes(ROUTE_NAMES.DOCUMENT_CHAT)) {
    //     navigate(ROUTE_NAMES.DOCUMENT_CHAT);
    //   } else {
    //     navigate(ROUTE_NAMES.KSCE_CHAT_SCREEN);
    //   }
    // }
  };

  const onPageChange = (page: number) => {
    resetData(page);
    dispatch(
      getThreads({
        params: {
          pdf_id: documentData?.id,
          page_no: page,
        },
        onSuccess: (res: any) => {
          const chat = res?.rows?.[0];
          // This means that against pdf id and page number a thread exists already
          if (res?.rows?.length) {
            navigate(`${ROUTE_NAMES.DRAWING_ANALYSIS_CHAT}/${chat.id}`);
          }
          // Means no thread exists already against this page of pdf
          else {
            navigate(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT);
            setShowWelcomeText(true);
          }
          // Once we successfully retrieve the thread id against pdf id and page number we update the thread list with default payload
          dispatch(
            getThreads({
              onSuccess: (res: any) => dispatch(updateThreads(res)),
            })
          );
        },
        onError: (err: any) => { },
      })
    );
  };

  // Compute isPolling to be true if either isPolling or isPollingVerifyChat is true
  const isPollingComputed = useMemo(
    () => isPolling || isPollingVerifyChat,
    [isPolling, isPollingVerifyChat]
  );

  return {
    t,
    submitHandler,
    inputRef,
    thraedUID,
    uid,
    threadId: currentThreadId,
    chatHeadersData,
    id,
    fetchThead,
    onChangehandler,
    input,
    loading,
    currentThread,
    maxChatSize,
    fileContainerRef,
    setInput,
    editSubmitHandler,
    isEditing,
    // handleCloseNotification,
    notificationVisibility,
    notificationContent,
    imagePopupState,
    onCloseImagePopup,
    likeChat,
    threadPID,
    modelOption,
    currentMessageResponse,
    setCurrentMessageResponse,
    isMessaging,
    setIsMessaging,
    setIsWritingFirstPatch,
    isWritingFirstPatch,
    selectedChat,
    setSelectedChat,
    urlType,
    setUrlType,
    onDrop,
    urls,
    setUrls,
    url,
    setUrl,
    fileLoading,
    filesPreview,
    setFilesPreview,
    removeFile,
    resetData, // Exported resetData function
    flagValidateUrl,
    setFlagValidateUrl,
    isPanelVisible,
    queryType,
    setQueryType,
    chatByIdResponse,
    recentThreads,
    getInitialThreads,
    fetchPdfPreview,
    pdfValidLink,
    pdfLinkLoading,
    setPdfLinkLoading,
    deepSearch,
    setDeepSearch,
    updateChatModeValue,
    chatMode,
    modeState,
    attachedFile,
    setAttachedFile,
    toggleRightSidebar,
    openProjectsListing,
    docChatMode,
    setDocChatMode,
    currentPathname,
    setCurrentPathname,
    selectedProject,
    setSelectedProject,
    getSource,
    getMode,
    sourcesData,
    setSourcesData,
    onProjectChange,
    getFilesPath,
    getDocNames,
    fetchAllDocs,
    documentsData,
    getDocumentsLoading,
    fetchSuggestedQuestions,
    suggestedQuestionsData,
    getSuggestedQuestionsLoading,
    downloadPdfOfThread,
    downloadPdfLoading,
    pdfPageNumber,
    setPdfPageNumber,
    getPdfPageNum,
    docsHistory,
    multiDocUploadView,
    setMultiDocUploadView,
    onPageChange,
    showWelcomeText,
    setShowWelcomeText,
    setPrevThread,
    fetchFaqs,
    getFaqsLoading,
    faqsData,
    fetchChats,
    getChatsLoading,
    chatsData,
    getFileNameMain,
    attachedFileProject,
    setAttachedFileProject,
    isTypewriterActive,
    setIsTypewriterActive,
    isPolling: isPollingComputed,
    setIsPolling,
    model,
    isPollingVerifyChat,
    setIsPollingVerifyChat,
    setModel,
    getModel,
    legalChatMode,
    setLegalMode,
    handleVerifyChat,
    getFileNamesArray,
    review,
    setReview,
    getFileId,
    getPdfId
  };
};
